/**
 * What each phase puts in front - a lookup, so nothing downstream branches
 * on the phase by name. A new difference between phases is a new field
 * here and one reader, never an `if (phase === ...)`.
 *
 * A phase rearranges and locks nothing: every tool stays reachable.
 */
import { DEFAULT_PHASE } from './frame';
import type { DrumId } from '../actions/catalog';
import type { Phase, PhaseFrame } from './types';

export interface PhaseLayout {
  /**
   * The map starts FOLDED behind the log: mounted, placing arrivals as it
   * always does, and one press away. Each viewer may open it for themselves.
   * Narrative only, on every layout: Combat and Stealth show the map (the
   * user, 2026-10-20 - Stealth's wedges, marks and rings are drawn there).
   */
  mapFolded: boolean;
  /** The livery follows the danger step (`danger.ts`): the root carries `data-danger`. */
  wearsDanger: boolean;
  /** Whether positions on the map count for the rules, unless the GM says otherwise (`frame.positions`). */
  positionsDefault: boolean;
  /** The livery follows Stealth's ladder (`engine/stealth/ladder`): the root carries `data-rung`. */
  wearsRung: boolean;
  /**
   * Players may tell the GM what they mean to do (`engine/narrative/intents`):
   * where there is no turn order to say whose word comes next.
   */
  takesIntents: boolean;
  /** Which plates the action drum is loaded with (`engine/actions/catalog` `DRUM_CATALOGS`). */
  drum: DrumId;
  /** A fight can be STARTED from here (`engine/phase/startFight`): the header offers it. */
  offersFight: boolean;
  /**
   * What the right column puts in front: the story's panels (the cast, the
   * board, clocks, notes), Stealth's (the ladder, the opposition) or the
   * target's. The others are one press away, per viewer (`hooks/useRightFront`).
   */
  rightFront: RightFront;
  /**
   * An attack on somebody outside the turn order stands for a round going
   * by, and counts their timed conditions down (under the round countdown
   * rule). Only where there are rounds: elsewhere time passes when the GM
   * says so (`time passes`).
   */
  attackCountdown: boolean;
}

type RightFront = 'story' | 'stealth' | 'target';

export const PHASE_LAYOUT: Record<Phase, PhaseLayout> = {
  narrative: {
    mapFolded: true,
    wearsDanger: false,
    wearsRung: false,
    drum: 'combat',
    takesIntents: true,
    positionsDefault: false,
    offersFight: true,
    rightFront: 'story',
    attackCountdown: false,
  },
  stealth: {
    mapFolded: false,
    wearsDanger: false,
    wearsRung: true,
    drum: 'stealth',
    takesIntents: true,
    // Stealth is played on the map: reach, range, cover and who hears a noise read it (2026-11-01, the user's ruling).
    positionsDefault: true,
    offersFight: true,
    rightFront: 'stealth',
    attackCountdown: false,
  },
  combat: {
    mapFolded: false,
    wearsDanger: true,
    wearsRung: false,
    drum: 'combat',
    takesIntents: false,
    positionsDefault: true,
    offersFight: false,
    rightFront: 'target',
    attackCountdown: true,
  },
};

/** The layout to draw; the default phase's while the phase is not known (nothing is visible then). */
export const layoutOf = (phase: Phase | null): PhaseLayout => PHASE_LAYOUT[phase ?? DEFAULT_PHASE];

/**
 * Does an attack count timed conditions down at this table right now? The
 * round countdown rule, where the phase has rounds for it to stand for.
 * While the phase is not known nothing is on screen to attack with; the
 * default phase's word stands.
 */
export function countsDownOnAttack(roundCountdown: boolean, frame: Pick<PhaseFrame, 'phase'> | null): boolean {
  return !!roundCountdown && layoutOf(frame?.phase ?? null).attackCountdown;
}

/**
 * DO POSITIONS COUNT at this table right now? The GM's word when given,
 * else the table's phase's default. Read by ONE seam (hooks/useShellGround),
 * which is what every rule that would ask the map goes through.
 */
export function positionsCount(frame: Pick<PhaseFrame, 'phase' | 'positions'>): boolean {
  return frame.positions ?? PHASE_LAYOUT[frame.phase].positionsDefault;
}
